import { useCallback, useState ,useEffect} from 'react';


export enum EnrollmentFlowStep {
  CORPORATE_INTRO = 'CORPORATE_INTRO',
  REQUEST_ACCESS_LIST = 'REQUEST_ACCESS_LIST',
  REQUEST_ACCESS_FORM = 'REQUEST_ACCESS_FORM',
  REQUEST_SUBMITTED = 'REQUEST_SUBMITTED',
  REQUEST_FAILED = 'REQUEST_FAILED',
  REVIEW_REQUEST = 'REVIEW_REQUEST',
  CLOSED = 'CLOSED',
}

interface UseCorporateEnrollmentFlowReturn {
  currentStep: EnrollmentFlowStep;
  isOpen: boolean;
  
  // Step navigation
  open: () => void;
  close: () => void;
  goToCorporateIntro: () => void;
  goToRequestAccessList: () => void;
  goToRequestAccessForm: (organizationId: string) => void;
  goToRequestSubmitted: () => void;
  goToRequestFailed: () => void;
  goToReviewRequest: () => void;
  
  // Selected data
  selectedOrganizationId?: string;
}

export const useCorporateEnrollmentFlow =
  (): UseCorporateEnrollmentFlowReturn => {
    const [currentStep, setCurrentStep] = useState<EnrollmentFlowStep>(
      () => {
        if (typeof window !== 'undefined') {
          const stored = sessionStorage.getItem('enrollmentFlowStep');
          if (stored === 'CREATE_OR_JOIN') {
            return EnrollmentFlowStep.REQUEST_ACCESS_LIST;
          }
          if (stored && Object.values(EnrollmentFlowStep).includes(stored as EnrollmentFlowStep)) {
            return stored as EnrollmentFlowStep;
          }
        }
        return EnrollmentFlowStep.CLOSED;
      }
    );
    const [selectedOrganizationId, setSelectedOrganizationId] = useState<
      string | undefined
    >(() => {
      if (typeof window !== 'undefined') {
        return sessionStorage.getItem('enrollmentOrganizationId') || undefined;
      }
      return undefined;
    });

    useEffect(() => {
      if (currentStep !== EnrollmentFlowStep.CLOSED) {
        sessionStorage.setItem('enrollmentFlowStep', currentStep);
      } else {
        sessionStorage.removeItem('enrollmentFlowStep');
      }
    }, [currentStep]);

    useEffect(() => {
      if (selectedOrganizationId) {
        sessionStorage.setItem('enrollmentOrganizationId', selectedOrganizationId);
      } else {
        sessionStorage.removeItem('enrollmentOrganizationId');
      }
    }, [selectedOrganizationId]);

    const open = useCallback(() => {
      setCurrentStep((prev) => 
        prev === EnrollmentFlowStep.CLOSED ? EnrollmentFlowStep.CORPORATE_INTRO : prev
      );
    }, []);

    const close = useCallback(() => {
      setCurrentStep(EnrollmentFlowStep.CLOSED);
      setSelectedOrganizationId(undefined);
    }, []);

    const goToCorporateIntro = useCallback(() => {
      setCurrentStep(EnrollmentFlowStep.CORPORATE_INTRO);
    }, []);

    const goToRequestAccessList = useCallback(() => {
      setCurrentStep(EnrollmentFlowStep.REQUEST_ACCESS_LIST);
    }, []);

    const goToRequestAccessForm = useCallback((organizationId: string) => {
      setSelectedOrganizationId(organizationId);
      setCurrentStep(EnrollmentFlowStep.REQUEST_ACCESS_FORM);
    }, []);

    const goToRequestSubmitted = useCallback(() => {
      setCurrentStep(EnrollmentFlowStep.REQUEST_SUBMITTED);
    }, []);

    const goToRequestFailed = useCallback(() => {
      setCurrentStep(EnrollmentFlowStep.REQUEST_FAILED);
    }, []);

    const goToReviewRequest = useCallback(() => {
      setCurrentStep(EnrollmentFlowStep.REVIEW_REQUEST);
    }, []);

    return {
      currentStep,
      isOpen: currentStep !== EnrollmentFlowStep.CLOSED,
      open,
      close,
      goToCorporateIntro,
      goToRequestAccessList,
      goToRequestAccessForm,
      goToRequestSubmitted,
      goToRequestFailed,
      goToReviewRequest,
      selectedOrganizationId,
    };
  };
